<template>
  <div class="notes-root">
    <section class="notes-card">
      <!-- ── Header ── -->
      <header class="notes-head">
        <div class="notes-head-left">
          <h2 class="notes-title">
            Notes
            <span class="notes-count">{{ notes.length }}</span>
          </h2>
        </div>

        <div class="notes-head-right">
          <div class="notes-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input v-model="searchQuery" type="search" placeholder="Search…" aria-label="Search notes" />
          </div>

          <button class="notes-btn notes-btn--ghost" type="button" @click="toggleSort" :aria-label="`Sort ${sortDir === 'desc' ? 'newest first' : 'oldest first'}`">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 6h18" />
              <path d="M7 12h10" />
              <path d="M11 18h2" />
            </svg>
            Sort
          </button>

          <button v-if="isAdmin" class="notes-btn notes-btn--primary" type="button" @click="openModal('add')">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
            Add note
          </button>
        </div>
      </header>

      <!-- ── Empty ── -->
      <div v-if="notes.length === 0" class="notes-empty">
        <div class="notes-empty-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M9 13h6" />
            <path d="M9 17h4" />
          </svg>
        </div>
        <h5 class="notes-empty-title">No notes yet</h5>
        <p class="notes-empty-text" v-if="isAdmin">Add your first note to keep a project log.</p>
        <button v-if="isAdmin" class="notes-btn notes-btn--primary" type="button" @click="openModal('add')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          Add your first note
        </button>
      </div>

      <!-- ── No search results ── -->
      <div v-else-if="filteredNotes.length === 0" class="notes-empty">
        <h5 class="notes-empty-title">No notes match “{{ searchQuery }}”</h5>
        <button class="notes-btn notes-btn--ghost" type="button" @click="searchQuery = ''">Clear search</button>
      </div>

      <!-- ── List ── -->
      <ul v-else class="notes-list">
        <li v-for="note in filteredNotes" :key="note.note_id" class="notes-row" tabindex="0">
          <div class="notes-date-block">
            <span class="notes-date-d">{{ formatDay(note.time) }}</span>
            <span class="notes-date-m">{{ formatMonth(note.time) }}</span>
          </div>

          <div class="notes-body">
            <div class="notes-content" v-html="note.note"></div>
          </div>

          <div class="notes-meta-col">
            <div class="notes-stamp">{{ formatRelative(note.time) }}</div>
            <div class="notes-edited">{{ formatFullDate(note.time) }}</div>
          </div>

          <div v-if="isAdmin" class="notes-actions">
            <button class="notes-icon-btn" type="button" aria-label="Edit note" @click="openModal('edit', note)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              </svg>
            </button>
            <button class="notes-icon-btn notes-icon-btn--danger" type="button" aria-label="Delete note" @click="deleteNote(note)">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="m19 6-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
            </button>
          </div>
        </li>
      </ul>
    </section>

    <transition name="notes-fade">
      <div v-if="showModal" class="notes-overlay" @click.self="closeModal" @keydown.esc="closeModal">
        <div class="notes-dialog" role="dialog" aria-modal="true" :aria-label="modalMode === 'add' ? 'Add note' : 'Edit note'">
          <div class="notes-dialog-head">
            <h5 class="notes-dialog-title">{{ modalMode === 'add' ? 'Add note' : 'Edit note' }}</h5>
            <button class="notes-dialog-close" type="button" aria-label="Close" @click="closeModal">✕</button>
          </div>

          <form class="notes-dialog-body" @submit.prevent="handleSubmit">
            <label class="notes-field-label">Note content</label>
            <div
              ref="noteEditable"
              contenteditable="true"
              class="notes-rich-input"
              @input="updateNoteData"
              placeholder="Enter your note here…"
            ></div>

            <template v-if="isAdmin && modalMode === 'edit'">
              <label class="notes-field-label" for="notes-date-input">Note date</label>
              <input
                id="notes-date-input"
                v-model="form.dateInput"
                type="date"
                class="notes-date-input"
                :max="todayInput"
                required
              />
            </template>
          </form>

          <div class="notes-dialog-foot">
            <button class="notes-btn notes-btn--ghost" type="button" @click="closeModal">Cancel</button>
            <button class="notes-btn notes-btn--primary" type="button" @click="handleSubmit">
              {{ modalMode === 'add' ? 'Add note' : 'Save changes' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { BACKEND_URL } from '../../variables';
import { mapState } from "pinia";
import { useUserStore } from "../../store/UserStore";

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default {
  props: {
    notes: { type: Array, required: false, default: () => [] },
    openTab: { type: String, required: false }
  },
  data() {
    return {
      showModal: false,
      modalMode: 'add',
      searchQuery: '',
      sortDir: 'desc',
      form: {
        note_id: null,
        note: '',
      }
    }
  },
  computed: {
    ...mapState(useUserStore, ["current_user"]),
    isAdmin() { return this.current_user?.role === 'admin'; },
    todayInput() { return this.toDateInput(Math.floor(Date.now() / 1000)); },
    filteredNotes() {
      const q = this.searchQuery.trim().toLowerCase();
      const filtered = q
        ? this.notes.filter(n => this.stripHtml(n.note).toLowerCase().includes(q))
        : [...this.notes];
      return filtered.sort((a, b) => {
        const ta = Number(a.time) || 0;
        const tb = Number(b.time) || 0;
        return this.sortDir === 'desc' ? tb - ta : ta - tb;
      });
    }
  },
  methods: {
    stripHtml(html) {
      if (!html) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
    toggleSort() {
      this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
    },
    updateNoteData(event) {
      this.form.note = event.target.innerHTML;
    },
    openModal(mode, note = null) {
      this.modalMode = mode;
      if (mode === 'edit' && note) {
        this.form = { ...note, dateInput: this.toDateInput(note.time) };
      } else {
        this.form = { note_id: null, note: '' };
      }
      this.showModal = true;
      this.$nextTick(() => {
        if (this.$refs.noteEditable) {
          this.$refs.noteEditable.innerHTML = this.form.note || '';
          this.$refs.noteEditable.focus();
        }
      });
    },
    closeModal() {
      this.showModal = false;
      this.form = { note_id: null, note: '' };
    },
    handleSubmit() {
      const vm = this;
      if (!this.form.note || this.form.note === '<br>') {
        alert('Please enter some text');
        return;
      }

      const url = (this.openTab === 'overview')
        ? `${BACKEND_URL}api/${this.modalMode === 'add' ? 'presale_utc_components_notes_add' : 'presale_utc_components_notes_update'}`
        : `${BACKEND_URL}api/add_update_mls_rental_note`;

      const formData = new FormData();
      formData.append("uid", this.$route.query.uid);
      formData.append("key", this.form.key);
      formData.append("note", this.form.note);
      if (this.modalMode === 'edit') {
        formData.append("note_id", this.form.note_id);
        const newTime = this.dateChangedTime();
        if (newTime) formData.append("note_time", newTime);
      }

      $.ajax({
        type: "POST",
        url: url,
        data: formData,
        processData: false,
        contentType: false,
      }).done(function () {
        vm.$emit('fetchData');
        vm.showModal = false;
        vm.$bvToast.toast("Success", { variant: "success" });
      }).fail(() => {
        alert("Upload failed");
      });
    },
    deleteNote(note) {
      const vm = this;
      if (!confirm('Delete this note?')) return;
      const fd = new FormData();
      fd.append("note_id", note.note_id);
      $.ajax({
        type: "POST",
        url: `${BACKEND_URL}api/presale_utc_components_notes_delete`,
        data: fd, processData: false, contentType: false,
      }).done(() => vm.$emit('fetchData'));
    },
    // Unix seconds -> "YYYY-MM-DD" in local time, for <input type="date">.
    toDateInput(ts) {
      const d = this.toDate(ts);
      if (!d) return '';
      const p = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    // New unix timestamp when an admin changed the date (original time of day is kept), else null.
    dateChangedTime() {
      if (!this.isAdmin || !this.form.dateInput) return null;
      if (this.form.dateInput === this.toDateInput(this.form.time)) return null;
      const [y, m, day] = this.form.dateInput.split('-').map(Number);
      const orig = this.toDate(this.form.time) || new Date();
      const d = new Date(y, m - 1, day, orig.getHours(), orig.getMinutes(), orig.getSeconds());
      return isNaN(d) ? null : Math.floor(d.getTime() / 1000);
    },
    toDate(ts) {
      const n = Number(ts);
      if (!n) return null;
      const d = new Date(n * 1000);
      return isNaN(d) ? null : d;
    },
    formatDay(ts) {
      const d = this.toDate(ts);
      return d ? String(d.getDate()).padStart(2, '0') : '—';
    },
    formatMonth(ts) {
      const d = this.toDate(ts);
      if (!d) return '';
      return `${MONTH_SHORT[d.getMonth()]} ${String(d.getFullYear()).slice(-2)}`;
    },
    formatFullDate(ts) {
      const d = this.toDate(ts);
      if (!d) return '';
      return `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    },
    formatRelative(ts) {
      const d = this.toDate(ts);
      if (!d) return '';
      const diffMs = Date.now() - d.getTime();
      const sec = Math.floor(diffMs / 1000);
      if (sec < 60) return 'just now';
      const min = Math.floor(sec / 60);
      if (min < 60) return `${min} ${min === 1 ? 'minute' : 'minutes'} ago`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr} ${hr === 1 ? 'hour' : 'hours'} ago`;
      const day = Math.floor(hr / 24);
      if (day < 7) return `${day} ${day === 1 ? 'day' : 'days'} ago`;
      const wk = Math.floor(day / 7);
      if (wk < 5) return `${wk} ${wk === 1 ? 'week' : 'weeks'} ago`;
      const mo = Math.floor(day / 30);
      if (mo < 12) return `${mo} ${mo === 1 ? 'month' : 'months'} ago`;
      const yr = Math.floor(day / 365);
      return `${yr} ${yr === 1 ? 'year' : 'years'} ago`;
    }
  }
}
</script>

<style lang="scss" scoped>
/* =========================================================
   RPD design tokens (project-local; mirror the design file)
   ========================================================= */
$rpd-navy:        #12283F;
$rpd-blue-violet: #343376;
$rpd-coral:       #FC5868;
$rpd-red:         #D43847;
$rpd-sky-blue:    #EDF2FE;
$surface:         #FFFFFF;
$surface-sunken:  #F7F8FB;
$fg-1:            #12283F;
$fg-2:            #545454;
$fg-3:            #8A8A8A;
$border:          #E9E8E9;
$border-strong:   #C9CCD2;
$divider:         #EEF0F4;
$success:         #1F9D55;

.notes-root {
  width: 100%;
  font-family: "Poppins", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
  color: $fg-1;
  -webkit-font-smoothing: antialiased;
}

/* ── Card ─────────────────────────────────────────────── */
.notes-card {
  background: $surface;
  border: 1px solid $border;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(18, 40, 63, 0.08), 0 1px 2px rgba(18, 40, 63, 0.04);
  overflow: hidden;
}

/* ── Header ───────────────────────────────────────────── */
.notes-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid $divider;
  flex-wrap: wrap;
}

.notes-title {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-family: inherit;
  font-weight: 700;
  font-size: 1.25rem;
  color: $fg-1;
  letter-spacing: -0.02em;
}

.notes-count {
  font-family: "JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 11px;
  background: $rpd-sky-blue;
  color: $rpd-blue-violet;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 500;
  line-height: 1.4;
}

.notes-head-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ── Search ───────────────────────────────────────────── */
.notes-search {
  position: relative;
  display: flex;
  align-items: center;
}

.notes-search svg {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  stroke-width: 1.75;
  color: $fg-3;
  pointer-events: none;
}

.notes-search input {
  appearance: none;
  width: 220px;
  height: 34px;
  padding: 0 10px 0 30px;
  border: 1px solid $border-strong;
  border-radius: 8px;
  background: $surface;
  font-family: inherit;
  font-size: 13px;
  color: $fg-1;
  transition: border-color 0.12s, box-shadow 0.12s;

  &::placeholder { color: $fg-3; }
  &:focus {
    outline: none;
    border-color: $rpd-navy;
    box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.22);
  }
}

/* ── Buttons ──────────────────────────────────────────── */
.notes-btn {
  appearance: none;
  cursor: pointer;
  font-family: inherit;
  font-weight: 600;
  font-size: 13px;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s, border-color 0.12s, box-shadow 0.12s;

  svg { width: 14px; height: 14px; stroke-width: 2; }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.32);
  }
}

.notes-btn--primary {
  background: $rpd-navy;
  color: #ffffff;
  border-color: $rpd-navy;

  &:hover { background: lighten($rpd-navy, 6%); }
}

.notes-btn--ghost {
  background: transparent;
  color: $fg-1;
  border-color: $border-strong;

  &:hover { background: $rpd-sky-blue; border-color: $rpd-navy; color: $rpd-navy; }
}

/* ── List ─────────────────────────────────────────────── */
.notes-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.notes-row {
  display: grid;
  grid-template-columns: 56px 1fr auto auto;
  align-items: start;
  gap: 18px;
  padding: 18px 20px;
  border-bottom: 1px solid $divider;
  transition: background 0.12s;
  position: relative;

  &:last-child { border-bottom: 0; }
  &:hover { background: $surface-sunken; }
  &:hover .notes-actions,
  &:focus-within .notes-actions { opacity: 1; }
}

/* Date block */
.notes-date-block {
  text-align: center;
  font-family: inherit;
  color: $fg-2;
  line-height: 1.1;
  padding-top: 2px;
}

.notes-date-d {
  display: block;
  font-weight: 700;
  font-size: 1.25rem;
  color: $fg-1;
  letter-spacing: -0.02em;
}

.notes-date-m {
  display: block;
  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $fg-3;
  margin-top: 3px;
}

/* Body */
.notes-body {
  min-width: 0;
}

.notes-content {
  font-family: "Helvetica", "Helvetica Neue", Arial, sans-serif;
  font-weight: 300;
  font-size: 14px;
  color: $fg-2;
  line-height: 1.5;
  max-width: 64ch;
  word-break: break-word;

  :deep(p) { margin: 0 0 6px; &:last-child { margin-bottom: 0; } }
  :deep(strong), :deep(b) { color: $fg-1; font-weight: 600; }
  :deep(a) { color: $rpd-blue-violet; text-decoration: underline; text-underline-offset: 2px; }
  :deep(ul), :deep(ol) { padding-left: 20px; margin: 4px 0; }
  :deep(code) {
    font-family: "JetBrains Mono", "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 0.92em;
    background: $surface-sunken;
    border: 1px solid $border;
    border-radius: 2px;
    padding: 1px 5px;
    color: $rpd-blue-violet;
  }
  :deep(img) { max-width: 100%; height: auto; border-radius: 4px; }
}

/* Meta column */
.notes-meta-col {
  text-align: right;
  font-family: "Helvetica", "Helvetica Neue", Arial, sans-serif;
  font-size: 12px;
  color: $fg-3;
  padding-top: 4px;
  white-space: nowrap;
}

.notes-stamp {
  font-family: "Poppins", inherit;
  font-weight: 500;
  color: $fg-2;
  font-size: 12px;
}

.notes-edited {
  font-size: 11px;
  margin-top: 2px;
}

/* Actions */
.notes-actions {
  display: inline-flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.12s;
}

.notes-icon-btn {
  appearance: none;
  background: transparent;
  border: 1px solid transparent;
  color: $fg-2;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.12s, color 0.12s, border-color 0.12s;

  svg { width: 16px; height: 16px; stroke-width: 1.75; }

  &:hover {
    background: $surface;
    border-color: $border-strong;
    color: $fg-1;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.32);
    opacity: 1;
  }

  &--danger:hover {
    background: rgba(212, 56, 71, 0.08);
    color: $rpd-red;
    border-color: rgba(212, 56, 71, 0.30);
  }
}

/* ── Empty state ──────────────────────────────────────── */
.notes-empty {
  padding: 56px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.notes-empty-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: $rpd-sky-blue;
  color: $rpd-blue-violet;
  display: grid;
  place-items: center;
  margin-bottom: 4px;

  svg { width: 22px; height: 22px; stroke-width: 1.5; }
}

.notes-empty-title {
  margin: 0;
  font-weight: 600;
  font-size: 1rem;
  color: $fg-1;
}

.notes-empty-text {
  margin: 0;
  font-family: "Helvetica", "Helvetica Neue", Arial, sans-serif;
  font-weight: 300;
  font-size: 14px;
  color: $fg-2;
  max-width: 44ch;
}

.notes-empty .notes-btn {
  margin-top: 8px;
}

/* ── Rich input (modal) ──────────────────────────────── */
.notes-rich-input {
  border: 1px solid $border-strong;
  padding: 12px 14px;
  border-radius: 8px;
  min-height: 140px;
  background: $surface;
  outline: none;
  width: 100%;
  font-family: "Helvetica", "Helvetica Neue", Arial, sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: $fg-1;
  line-height: 1.5;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:focus {
    border-color: $rpd-navy;
    box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.22);
  }

  &:empty:before {
    content: attr(placeholder);
    color: $fg-3;
  }
}

/* ── Add / edit dialog (matches the app's sp-modal look) ─── */
.notes-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 16px;
  overflow-y: auto;
  background: rgba(18, 40, 63, 0.55);
}

.notes-dialog {
  width: 100%;
  max-width: 560px;
  background: $surface;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 24px 60px -20px rgba(14, 27, 44, 0.28);
}

.notes-dialog-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid $border;
}

.notes-dialog-title {
  margin: 0;
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: $fg-1;
}

.notes-dialog-close {
  margin-left: auto;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #7E8B97;
  padding: 4px 8px;
  border-radius: 6px;

  &:hover { background: #F4F6FA; }
}

.notes-dialog-body {
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.notes-field-label {
  margin: 8px 0 0;
  font-size: 12px;
  font-weight: 600;
  color: $fg-2;

  &:first-child { margin-top: 0; }
}

.notes-date-input {
  appearance: none;
  height: 38px;
  padding: 0 12px;
  max-width: 220px;
  border: 1px solid $border-strong;
  border-radius: 8px;
  background: $surface;
  font-family: inherit;
  font-size: 13px;
  color: $fg-1;
  transition: border-color 0.12s, box-shadow 0.12s;

  &:focus {
    outline: none;
    border-color: $rpd-navy;
    box-shadow: 0 0 0 3px rgba(52, 51, 118, 0.22);
  }
}

.notes-dialog-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 22px;
  border-top: 1px solid $border;
}

.notes-fade-enter-active,
.notes-fade-leave-active { transition: opacity 0.15s; }
.notes-fade-enter,
.notes-fade-leave-to { opacity: 0; }

/* ── Responsive ──────────────────────────────────────── */
@media (max-width: 720px) {
  .notes-head { flex-direction: column; align-items: stretch; gap: 12px; }
  .notes-head-right { width: 100%; }
  .notes-search { flex: 1 1 100%; }
  .notes-search input { width: 100%; }
  .notes-row {
    grid-template-columns: 56px 1fr;
    grid-template-areas:
      "date body"
      "meta meta"
      "act  act";
    gap: 10px 14px;
  }
  .notes-date-block { grid-area: date; }
  .notes-body { grid-area: body; }
  .notes-meta-col { grid-area: meta; text-align: left; padding-top: 0; }
  .notes-actions { grid-area: act; opacity: 1; justify-self: end; }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { transition-duration: 0 !important; }
}

/* ── Dark mode ───────────────────────────────────────── */
html.dark .notes-card { background: #152030; border-color: rgba(255, 255, 255, 0.08); box-shadow: none; }
html.dark .notes-head { border-bottom-color: rgba(255, 255, 255, 0.08); }
html.dark .notes-title { color: #D6E4F0; }
html.dark .notes-count { background: rgba(165, 168, 232, 0.18); color: #A5A8E8; }
html.dark .notes-search input {
  background: #0E1B2C;
  border-color: rgba(255, 255, 255, 0.10);
  color: #D6E4F0;
}
html.dark .notes-search input::placeholder { color: rgba(214, 228, 240, 0.45); }
html.dark .notes-search input:focus { border-color: #A5A8E8; box-shadow: 0 0 0 3px rgba(165, 168, 232, 0.22); }
html.dark .notes-search svg { color: rgba(214, 228, 240, 0.55); }
html.dark .notes-btn--primary {
  background: #D6E4F0;
  color: #12283F;
  border-color: #D6E4F0;
}
html.dark .notes-btn--primary:hover { background: #FFFFFF; }
html.dark .notes-btn--ghost {
  color: #D6E4F0;
  border-color: rgba(255, 255, 255, 0.14);
}
html.dark .notes-btn--ghost:hover {
  background: rgba(165, 168, 232, 0.12);
  border-color: #A5A8E8;
  color: #D6E4F0;
}
html.dark .notes-row { border-bottom-color: rgba(255, 255, 255, 0.06); }
html.dark .notes-row:hover { background: rgba(255, 255, 255, 0.03); }
html.dark .notes-date-d { color: #D6E4F0; }
html.dark .notes-date-m { color: rgba(214, 228, 240, 0.55); }
html.dark .notes-content { color: rgba(214, 228, 240, 0.78); }
html.dark .notes-content :deep(strong),
html.dark .notes-content :deep(b) { color: #D6E4F0; }
html.dark .notes-content :deep(a) { color: #A5A8E8; }
html.dark .notes-content :deep(code) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.10);
  color: #A5A8E8;
}
html.dark .notes-meta-col { color: rgba(214, 228, 240, 0.50); }
html.dark .notes-stamp { color: rgba(214, 228, 240, 0.72); }
html.dark .notes-icon-btn { color: rgba(214, 228, 240, 0.65); }
html.dark .notes-icon-btn:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.14);
  color: #D6E4F0;
}
html.dark .notes-icon-btn--danger:hover {
  background: rgba(248, 113, 113, 0.14);
  border-color: rgba(248, 113, 113, 0.30);
  color: #F87085;
}
html.dark .notes-empty-title { color: #D6E4F0; }
html.dark .notes-empty-text { color: rgba(214, 228, 240, 0.65); }
html.dark .notes-empty-icon {
  background: rgba(165, 168, 232, 0.14);
  color: #A5A8E8;
}
html.dark .notes-overlay { background: rgba(0, 0, 0, 0.65); }
html.dark .notes-dialog { background: #152030; }
html.dark .notes-dialog-head,
html.dark .notes-dialog-foot { border-color: rgba(255, 255, 255, 0.08); }
html.dark .notes-dialog-title { color: #D6E4F0; }
html.dark .notes-dialog-close { color: rgba(214, 228, 240, 0.6); }
html.dark .notes-dialog-close:hover { background: #1D3048; }
html.dark .notes-field-label { color: rgba(214, 228, 240, 0.72); }
html.dark .notes-date-input {
  background: #0E1B2C;
  border-color: rgba(255, 255, 255, 0.10);
  color: #D6E4F0;
  color-scheme: dark;
}
html.dark .notes-date-input:focus { border-color: #A5A8E8; box-shadow: 0 0 0 3px rgba(165, 168, 232, 0.22); }
html.dark .notes-rich-input {
  background: #0E1B2C;
  border-color: rgba(255, 255, 255, 0.10);
  color: #D6E4F0;
}
html.dark .notes-rich-input:empty:before { color: rgba(214, 228, 240, 0.45); }
html.dark .notes-rich-input:focus {
  border-color: #A5A8E8;
  box-shadow: 0 0 0 3px rgba(165, 168, 232, 0.22);
}
</style>
